import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, X, PieChart, Pencil } from 'lucide-react';
import { LoadingButton } from '../components/QuickWins';
import { PartnerDropdown } from '../components/PartnerDropdown';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showNewSubCategoryInput, setShowNewSubCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSubCategoryName, setNewSubCategoryName] = useState('');
  const [latestConversionRate, setLatestConversionRate] = useState(null);
  const [lastExpenseRate, setLastExpenseRate] = useState(null);
  const [rateError, setRateError] = useState('');
  const [editRateError, setEditRateError] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    sub_category: '',
    amount: '',
    currency: 'INR',
    conversion_rate: '',
    cycle_id: '',
    payment_source: 'cash_pool',
    partner_id: '',
    notes: ''
  });

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, cyclesRes, categoriesRes, summaryRes, lastRateRes] = await Promise.all([
        axiosInstance.get('/expenses'),
        axiosInstance.get('/cycles'),
        axiosInstance.get('/expense-categories'),
        axiosInstance.get('/expenses/summary'),
        axiosInstance.get('/expenses/last-inr-rate')
      ]);
      setExpenses(expensesRes.data);
      setCycles(cyclesRes.data);
      setCategories(categoriesRes.data);
      setSummary(summaryRes.data);

      // Latest closed-cycle rate for prefill; last-used expense rate as hint.
      const latestRate = lastRateRes.data?.latest_cycle_rate ?? null;
      const lastRate = lastRateRes.data?.last_expense_rate ?? null;
      setLatestConversionRate(latestRate);
      setLastExpenseRate(lastRate);
      setFormData(prev => ({
        ...prev,
        conversion_rate: latestRate != null ? String(latestRate) : ''
      }));
    } catch (error) {
      toast.error('Failed to fetch expenses data');
    } finally {
      setLoading(false);
    }
  };

  const getSubCategories = () => {
    const cat = categories.find(c => c.category === formData.category);
    return cat?.sub_categories || [];
  };

  const handleAddNewCategory = () => {
    if (newCategoryName.trim()) {
      setCategories([...categories, { category: newCategoryName.trim(), sub_categories: [] }]);
      setFormData({ ...formData, category: newCategoryName.trim(), sub_category: '' });
      setNewCategoryName('');
      setShowNewCategoryInput(false);
    }
  };

  const handleAddNewSubCategory = () => {
    if (newSubCategoryName.trim() && formData.category) {
      const updatedCategories = categories.map(cat => {
        if (cat.category === formData.category) {
          return { ...cat, sub_categories: [...cat.sub_categories, newSubCategoryName.trim()] };
        }
        return cat;
      });
      setCategories(updatedCategories);
      setFormData({ ...formData, sub_category: newSubCategoryName.trim() });
      setNewSubCategoryName('');
      setShowNewSubCategoryInput(false);
    }
  };

  const RATE_MIN = 60;
  const RATE_MAX = 130;

  const validateRate = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return 'Conversion rate is required for INR expenses';
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n <= 0) return 'Conversion rate must be > 0';
    if (n < RATE_MIN || n > RATE_MAX) return `Rate must be between ${RATE_MIN} and ${RATE_MAX}`;
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let sendRate = null;
    if (formData.currency === 'INR') {
      const err = validateRate(formData.conversion_rate);
      if (err) {
        setRateError(err);
        toast.error(err);
        return;
      }
      sendRate = parseFloat(formData.conversion_rate);
    }
    try {
      await axiosInstance.post('/expenses', {
        date: formData.date,
        category: formData.category,
        sub_category: formData.sub_category || null,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        conversion_rate: sendRate,
        cycle_id: formData.cycle_id || null,
        payment_source: formData.payment_source,
        partner_id: (formData.payment_source === 'cash_pool' && formData.currency === 'INR') ? formData.partner_id : null,
        notes: formData.notes
      });
      toast.success('Expense submitted for approval');
      fetchData();
      closeForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setShowNewCategoryInput(false);
    setShowNewSubCategoryInput(false);
    setNewCategoryName('');
    setNewSubCategoryName('');
    setRateError('');
    setFormData({
      date: new Date().toISOString().split('T')[0],
      category: '',
      sub_category: '',
      amount: '',
      currency: 'INR',
      conversion_rate: latestConversionRate != null ? String(latestConversionRate) : '',
      cycle_id: '',
      payment_source: 'cash_pool',
      partner_id: '',
      notes: ''
    });
  };

  // Edit expense handlers
  const openEditModal = (expense) => {
    setEditExpense({
      ...expense,
      conversion_rate: expense.conversion_rate != null
        ? String(expense.conversion_rate)
        : (latestConversionRate != null ? String(latestConversionRate) : ''),
    });
    setEditReason('');
    setEditRateError('');
    setShowEditModal(true);
  };

  const getEditSubCategories = () => {
    if (!editExpense) return [];
    const cat = categories.find(c => c.category === editExpense.category);
    return cat?.sub_categories || [];
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    const wasChangesRequested = editExpense.approval_status === 'changes_requested';
    const isApproved = editExpense.approval_status !== 'pending';
    
    if (isApproved && !editReason.trim()) {
      toast.error(
        wasChangesRequested
          ? 'Please add a resubmit note describing what changed'
          : 'Please provide a reason for editing this approved expense'
      );
      return;
    }

    let sendRate = null;
    if (editExpense.currency === 'INR') {
      const err = validateRate(editExpense.conversion_rate);
      if (err) {
        setEditRateError(err);
        toast.error(err);
        return;
      }
      sendRate = parseFloat(editExpense.conversion_rate);
    }
    
    setIsSubmitting(true);
    try {
      await axiosInstance.put(`/expenses/${editExpense.id}`, {
        date: editExpense.date,
        category: editExpense.category,
        sub_category: editExpense.sub_category || null,
        amount: parseFloat(editExpense.amount),
        currency: editExpense.currency,
        conversion_rate: sendRate,
        cycle_id: editExpense.cycle_id || null,
        payment_source: editExpense.payment_source,
        notes: editExpense.notes,
        edit_reason: isApproved ? editReason : undefined
      });
      if (wasChangesRequested) {
        toast.success('Resubmitted for approval');
      } else {
        toast.success(`Expense updated successfully${isApproved ? ' (with audit trail)' : ''}`);
      }
      setShowEditModal(false);
      setEditExpense(null);
      setEditReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="expenses-page">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Operating Costs</p>
            <h1 className="cb-h1">Expenses</h1>
          </div>
          <div className="flex gap-3 items-center">
            <div className="cb-segment">
              <button
                onClick={() => setActiveTab('list')}
                className={`cb-btn-secondary ${activeTab === 'list' ? 'cb-btn-secondary--active' : ''}`}
                style={{ border: 'none' }}
              >
                List View
              </button>
              <button
                onClick={() => setActiveTab('summary')}
                className={`cb-btn-secondary ${activeTab === 'summary' ? 'cb-btn-secondary--active' : ''}`}
                style={{ border: 'none' }}
              >
                Summary
              </button>
            </div>
            <button
              onClick={() => setShowForm(true)}
              data-testid="add-expense-button"
              className="cb-btn-primary"
            >
              <Plus className="w-4 h-4" />
              Add Expense
            </button>
          </div>
        </div>
        <hr className="cb-divider" />
      </div>

      {/* Summary View */}
      {activeTab === 'summary' && summary && (
        <div className="space-y-6 mb-6">
          {/* Total Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="cb-card">
              <p className="cb-card-label">Total Expenses ({summary.year})</p>
              <p className="cb-card-value cb-num">{summary.total.total_usdt.toLocaleString()} USDT</p>
              <p className="cb-muted" style={{ fontSize: '0.8125rem', marginTop: 'var(--cb-space-1)' }}>{summary.total.count} transactions</p>
            </div>
            <div className="cb-card">
              <p className="cb-card-label">INR Expenses</p>
              <p className="cb-card-value cb-num">₹{summary.total.total_inr.toLocaleString()}</p>
            </div>
            <div className="cb-card">
              <p className="cb-card-label">Categories</p>
              <p className="cb-card-value cb-num">{summary.by_category?.length || 0}</p>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="cb-card">
            <h3 className="cb-section-title mb-4 flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
              <PieChart className="w-5 h-5 cb-card-icon" />
              Category-wise Breakdown
            </h3>
            <div className="overflow-x-auto">
              <table className="cb-table" style={{ minWidth: '100%' }}>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Sub-Category</th>
                    <th className="cb-num">Count</th>
                    <th className="cb-num">INR Amount</th>
                    <th className="cb-num">USDT Equiv.</th>
                    <th className="cb-num">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_category?.map((cat, idx) => (
                    <React.Fragment key={idx}>
                      <tr style={{ background: 'var(--cb-hover)' }}>
                        <td style={{ fontWeight: 500 }}>{cat.category}</td>
                        <td className="cb-muted">-</td>
                        <td className="cb-num cb-muted">{cat.count}</td>
                        <td className="cb-num">₹{cat.total_inr.toLocaleString()}</td>
                        <td className="cb-num">{cat.total_usdt.toLocaleString()}</td>
                        <td className="cb-num cb-muted">
                          {summary.total.total_usdt > 0 ? ((cat.total_usdt / summary.total.total_usdt) * 100).toFixed(1) : 0}%
                        </td>
                      </tr>
                      {cat.sub_categories?.map((sub, subIdx) => (
                        <tr key={`${idx}-${subIdx}`}>
                          <td className="cb-muted" style={{ paddingLeft: 'var(--cb-space-6)' }}>↳</td>
                          <td className="cb-muted">{sub.name}</td>
                          <td className="cb-num cb-muted">{sub.count}</td>
                          <td className="cb-num cb-muted">-</td>
                          <td className="cb-num">{sub.total_usdt?.toLocaleString()}</td>
                          <td className="cb-num cb-muted">
                            {cat.total_usdt > 0 ? ((sub.total_usdt / cat.total_usdt) * 100).toFixed(0) : 0}%
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* List View */}
      {activeTab === 'list' && (
        <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table data-testid="expenses-table" className="cb-table" style={{ minWidth: '100%' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Sub-Category</th>
                <th>Currency</th>
                <th className="cb-num">Amount</th>
                <th className="cb-num">USDT</th>
                <th>Source</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center cb-muted">No expenses found</td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td className="whitespace-nowrap cb-num cb-muted" style={{ fontSize: '0.8125rem' }}>{new Date(expense.date).toLocaleDateString()}</td>
                    <td style={{ fontWeight: 500 }}>{expense.category}</td>
                    <td className="cb-muted">{expense.sub_category || '-'}</td>
                    <td>{expense.currency}</td>
                    <td className="cb-num">{expense.amount.toLocaleString()}</td>
                    <td className="cb-num cb-muted">{expense.usdt_equivalent ? expense.usdt_equivalent.toLocaleString() : '-'}</td>
                    <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>{expense.payment_source?.replace('_', ' ')}</td>
                    <td>
                      {expense.approval_status === 'changes_requested' ? (
                        <span
                          className="cb-pill"
                          style={{ fontSize: '0.6875rem', borderColor: 'var(--cb-warning, #b47a00)', color: 'var(--cb-warning, #b47a00)' }}
                          title={(expense.review_notes||[]).slice(-1).map(n => `${n.by}: ${n.note}`).join('') || 'Sent back for changes'}
                          data-testid={`changes-requested-badge-${expense.id}`}
                        >
                          changes requested
                        </span>
                      ) : (
                        <span className={`cb-pill ${
                          expense.approval_status === 'approved' ? 'cb-pill--positive'
                          : expense.approval_status === 'rejected' ? 'cb-pill--negative'
                          : ''
                        }`}>
                          {expense.approval_status}
                        </span>
                      )}
                      {expense.last_edited_at && (
                        <span className="cb-muted" style={{ marginLeft: 'var(--cb-space-1)', fontSize: '0.6875rem' }} title={`Edited by ${expense.last_edited_by}`}>
                          (edited)
                        </span>
                      )}
                    </td>
                    <td className="cb-muted max-w-[150px] truncate" style={{ fontSize: '0.8125rem' }}>{expense.notes || '-'}</td>
                    <td>
                      {(expense.approval_status === 'pending' || user.is_admin || (expense.approval_status === 'changes_requested' && expense.created_by === user.id)) && (
                        <button
                          onClick={() => openEditModal(expense)}
                          className="cb-btn-secondary"
                          style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                          title={
                            expense.approval_status === 'changes_requested'
                              ? 'Resubmit with changes'
                              : expense.approval_status !== 'pending'
                                ? 'Admin edit (requires reason)'
                                : 'Edit expense'
                          }
                          data-testid={`edit-expense-${expense.id}`}
                        >
                          <Pencil className="w-3 h-3" />
                          {expense.approval_status === 'changes_requested' ? 'Resubmit' : 'Edit'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Expense Modal */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="cb-section-title">Add Expense</h2>
              <button onClick={closeForm} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    required
                    data-testid="expense-date-input"
                    className="form-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>

                <div>
                  <label className="form-label">Category *</label>
                  {showNewCategoryInput ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="form-input flex-1"
                        placeholder="New category name"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleAddNewCategory}
                        className="cb-btn-primary"
                        style={{ padding: '8px 12px' }}
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowNewCategoryInput(false); setNewCategoryName(''); }}
                        className="cb-btn-secondary"
                        style={{ padding: '8px 12px' }}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <select
                        required
                        data-testid="expense-category-select"
                        className="form-input flex-1"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value, sub_category: '' })}
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat.category} value={cat.category}>{cat.category}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowNewCategoryInput(true)}
                        className="cb-btn-secondary"
                        style={{ padding: '8px 12px' }}
                        title="Add new category"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label">Sub-Category</label>
                {showNewSubCategoryInput ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="form-input flex-1"
                      placeholder="New sub-category name"
                      value={newSubCategoryName}
                      onChange={(e) => setNewSubCategoryName(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleAddNewSubCategory}
                      className="cb-btn-primary"
                      style={{ padding: '8px 12px' }}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowNewSubCategoryInput(false); setNewSubCategoryName(''); }}
                      className="cb-btn-secondary"
                      style={{ padding: '8px 12px' }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select
                      data-testid="expense-subcategory-select"
                      className="form-input flex-1"
                      value={formData.sub_category}
                      onChange={(e) => setFormData({ ...formData, sub_category: e.target.value })}
                      disabled={!formData.category}
                    >
                      <option value="">Select Sub-Category</option>
                      {getSubCategories().map((sub) => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowNewSubCategoryInput(true)}
                      className="cb-btn-secondary"
                      style={{ padding: '8px 12px' }}
                      title="Add new sub-category"
                      disabled={!formData.category}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Currency *</label>
                  <select
                    required
                    data-testid="expense-currency-select"
                    className="form-input"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  >
                    <option value="INR">INR</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Amount *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    data-testid="expense-amount-input"
                    className="form-input"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="Enter amount"
                  />
                </div>
              </div>

              {formData.currency === 'INR' && (
                <div>
                  <label className="form-label">
                    Conversion Rate (INR per USDT) <span style={{ color: 'var(--cb-negative)' }}>*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min={RATE_MIN}
                    max={RATE_MAX}
                    required
                    data-testid="expense-conversion-rate-input"
                    className="form-input"
                    value={formData.conversion_rate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData({ ...formData, conversion_rate: v });
                      setRateError(v === '' ? '' : validateRate(v));
                    }}
                    placeholder={`e.g. ${latestConversionRate ?? 94}`}
                  />
                  <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                    {latestConversionRate != null && (
                      <span>Latest cycle rate: <strong>{latestConversionRate}</strong></span>
                    )}
                    {lastExpenseRate != null && (
                      <span style={{ marginLeft: latestConversionRate != null ? 'var(--cb-space-3)' : 0 }}>
                        Last used on an expense: <strong data-testid="last-expense-rate-hint">{lastExpenseRate}</strong>
                      </span>
                    )}
                  </p>
                  {rateError && (
                    <p style={{ color: 'var(--cb-negative)', fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }} data-testid="expense-rate-error">
                      {rateError}
                    </p>
                  )}
                  {formData.amount && parseFloat(formData.conversion_rate) > 0 && (
                    <div style={{ marginTop: 'var(--cb-space-2)', padding: 'var(--cb-space-3)', background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)' }}>
                      <p className="cb-num" style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                        <strong>USDT Equivalent:</strong> {(parseFloat(formData.amount) / parseFloat(formData.conversion_rate)).toFixed(2)} USDT
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Payment Source</label>
                  <select
                    data-testid="expense-source-select"
                    className="form-input"
                    value={formData.payment_source}
                    onChange={(e) => setFormData({ ...formData, payment_source: e.target.value })}
                  >
                    <option value="cash_pool">Cash Pool</option>
                    <option value="exchange">Exchange</option>
                  </select>
                </div>
                {formData.payment_source === 'cash_pool' && formData.currency === 'INR' && (
                  <PartnerDropdown
                    value={formData.partner_id}
                    onChange={(id) => setFormData({ ...formData, partner_id: id })}
                    required
                    label="Paid by partner"
                    testId="expense-partner-select"
                  />
                )}
                <div>
                  <label className="form-label">Related Cycle</label>
                  <select
                    data-testid="expense-cycle-select"
                    className="form-input"
                    value={formData.cycle_id}
                    onChange={(e) => setFormData({ ...formData, cycle_id: e.target.value })}
                  >
                    <option value="">None</option>
                    {cycles.map((cycle) => (
                      <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">Notes</label>
                <textarea
                  data-testid="expense-notes-input"
                  className="form-input"
                  rows="2"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={closeForm} className="cb-btn-secondary">
                  Cancel
                </button>
                <button type="submit" data-testid="expense-submit-button" className="cb-btn-primary">
                  Submit for Approval
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Expense Modal */}
      {showEditModal && editExpense && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(11,11,10,0.55)' }} onClick={() => setShowEditModal(false)}>
          <div className="cb-card w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="cb-section-title">
                  {editExpense.approval_status === 'changes_requested' ? 'Resubmit Expense' : 'Edit Expense'}
                </h2>
                {editExpense.approval_status === 'changes_requested' ? (
                  <p className="cb-section-sub" style={{ color: 'var(--cb-warning, #b47a00)' }}>Address reviewer's notes and resubmit for approval</p>
                ) : editExpense.approval_status !== 'pending' && (
                  <p className="cb-section-sub" style={{ color: 'var(--cb-mark)' }}>Editing approved expense - reason required</p>
                )}
              </div>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-6 h-6" />
              </button>
            </div>

            {editExpense.approval_status === 'changes_requested' && (editExpense.review_notes || []).length > 0 && (
              <div
                data-testid="reviewer-notes-banner"
                style={{
                  background: 'var(--cb-bone)',
                  padding: 'var(--cb-space-3)',
                  borderRadius: 'var(--cb-radius)',
                  border: '1px solid var(--cb-warning, #b47a00)',
                  marginBottom: 'var(--cb-space-4)',
                }}
              >
                <p className="cb-eyebrow" style={{ color: 'var(--cb-warning, #b47a00)', marginBottom: 'var(--cb-space-2)' }}>
                  Reviewer Note
                </p>
                {(() => {
                  const latest = (editExpense.review_notes || []).slice().reverse().find(n => n.action === 'changes_requested') || (editExpense.review_notes || [])[editExpense.review_notes.length - 1];
                  return (
                    <>
                      <p style={{ fontSize: '0.875rem', color: 'var(--cb-ink)', marginBottom: 'var(--cb-space-1)' }}>
                        {latest?.note || '(no note provided)'}
                      </p>
                      <p className="cb-muted" style={{ fontSize: '0.75rem' }}>
                        — {latest?.by || 'reviewer'}{latest?.at ? ` · ${new Date(latest.at).toLocaleString()}` : ''}
                      </p>
                    </>
                  );
                })()}
              </div>
            )}
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editExpense.date}
                    onChange={(e) => setEditExpense({ ...editExpense, date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Category</label>
                  <select
                    className="form-input"
                    value={editExpense.category}
                    onChange={(e) => setEditExpense({ ...editExpense, category: e.target.value, sub_category: '' })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.category} value={cat.category}>{cat.category}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Sub-Category</label>
                  <select
                    className="form-input"
                    value={editExpense.sub_category || ''}
                    onChange={(e) => setEditExpense({ ...editExpense, sub_category: e.target.value })}
                  >
                    <option value="">None</option>
                    {getEditSubCategories().map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Currency</label>
                  <select
                    className="form-input"
                    value={editExpense.currency}
                    onChange={(e) => setEditExpense({ ...editExpense, currency: e.target.value })}
                  >
                    <option value="INR">INR</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={editExpense.amount}
                    onChange={(e) => setEditExpense({ ...editExpense, amount: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Payment Source</label>
                  <select
                    className="form-input"
                    value={editExpense.payment_source}
                    onChange={(e) => setEditExpense({ ...editExpense, payment_source: e.target.value })}
                  >
                    <option value="cash_pool">Cash Pool</option>
                    <option value="exchange">Exchange</option>
                  </select>
                </div>
              </div>

              {editExpense.currency === 'INR' && (
                <div>
                  <label className="form-label">
                    Conversion Rate (INR per USDT) <span style={{ color: 'var(--cb-negative)' }}>*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min={RATE_MIN}
                    max={RATE_MAX}
                    required
                    data-testid="edit-expense-conversion-rate-input"
                    className="form-input"
                    value={editExpense.conversion_rate || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditExpense({ ...editExpense, conversion_rate: v });
                      setEditRateError(v === '' ? '' : validateRate(v));
                    }}
                    placeholder={`e.g. ${latestConversionRate ?? 94}`}
                  />
                  <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                    {latestConversionRate != null && (
                      <span>Latest cycle rate: <strong>{latestConversionRate}</strong></span>
                    )}
                    {lastExpenseRate != null && (
                      <span style={{ marginLeft: latestConversionRate != null ? 'var(--cb-space-3)' : 0 }}>
                        Last used on an expense: <strong>{lastExpenseRate}</strong>
                      </span>
                    )}
                  </p>
                  {editRateError && (
                    <p style={{ color: 'var(--cb-negative)', fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }} data-testid="edit-expense-rate-error">
                      {editRateError}
                    </p>
                  )}
                  {editExpense.amount && parseFloat(editExpense.conversion_rate) > 0 && (
                    <p className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                      USDT: {(parseFloat(editExpense.amount) / parseFloat(editExpense.conversion_rate)).toFixed(2)}
                    </p>
                  )}
                </div>
              )}
              
              <div>
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={editExpense.notes || ''}
                  onChange={(e) => setEditExpense({ ...editExpense, notes: e.target.value })}
                />
              </div>
              
              {/* Reason field for approved expenses */}
              {editExpense.approval_status !== 'pending' && (
                <div style={{ background: 'var(--cb-bone)', padding: 'var(--cb-space-3)', borderRadius: 'var(--cb-radius)', border: '1px solid var(--cb-line)' }}>
                  <label className="form-label" style={{ color: 'var(--cb-ink)' }}>
                    {editExpense.approval_status === 'changes_requested' ? 'Resubmit Note' : 'Edit Reason'} <span style={{ color: 'var(--cb-negative)' }}>*</span>
                  </label>
                  <textarea
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    className="form-input"
                    rows={2}
                    placeholder={editExpense.approval_status === 'changes_requested'
                      ? "Describe what changed in response to the reviewer's note (required)"
                      : 'Explain why you are editing this approved expense (required for audit trail)'}
                    required
                    data-testid="expense-edit-reason"
                  />
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="cb-btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <LoadingButton type="submit" loading={isSubmitting} className="cb-btn-primary" data-testid="save-expense-edit">
                  {editExpense.approval_status === 'changes_requested' ? 'Resubmit for Approval' : 'Save Changes'}
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
